import { DateRangeBox } from 'devextreme-react';
import { Column, DataGrid } from 'devextreme-react/data-grid';
import { LoadPanel } from 'devextreme-react/load-panel';
import { TextBox } from 'devextreme-react/text-box';
import ExcelJS from 'exceljs';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Col, Label, Row } from 'reactstrap';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import api from '../../services/api-configuration-v3';
import { numberWithCommas } from '../../utils/NumberFormat';
import generateAndDownloadGrupajPDF from './GrupajPDFGenerator';

const INITIAL_SUMMARY_STATE = {
    toplamYukSayisi: 0,
    sevkeHazirYukSayisi: 0,
    eslenenTirSayisi: 0,
    eslenmemisTirSayisi: 0,
    hazirYuklerdenEslenenSayisi: '0/0'
};

const VALIDATION_RULES = {
    PLATE: /^[0-9]{2}[A-Z]+[0-9]+$/,
    PHONE: /^\(\d{3}\)\s\d{3}-\d{4}$/
};

const ERROR_MESSAGES = {
    PLATE_EMPTY: 'Plaka boş olamaz',
    PLATE_FORMAT: 'Geçerli bir plaka formatı giriniz. Örn: 34ABC123',
    DRIVER_EMPTY: 'Şoför adı boş olamaz',
    PHONE_FORMAT: '10 Haneli bir telefon numarası giriniz. Örn: 5XXXXXXXXX'
};

/**
 * Filter Section Component
 * Handles the date range and groupage number filters
 */
const FilterSection = ({ filters, onFilterChange, onSearch }) => (
    <div className="filter-section h-100">
        <div className="filter-header">
            <i className="fa fa-filter"></i>
            <span>Filtrele</span>
        </div>
        <div className="filter-body d-flex flex-column">
            <div className="flex-grow-1">
                <div className="filter-group">
                    <Label>Planlanan Sevk Tarihi Aralığı</Label>
                    <DateRangeBox
                        id='shipping_date_planned'
                        startDateLabel="Başlangıç"
                        endDateLabel="Bitiş"
                        // def={filters.shipping_start}
                        //endDate={filters.shipping_end}
                        //onValueChanged={(e) => {
                        //  onFilterChange({ target: { name: 'shipping_start', value: e.value?.startDate ? moment(e.value.startDate).format('YYYY-MM-DD') : null } });
                        // onFilterChange({ target: { name: 'shipping_end', value: e.value?.endDate ? moment(e.value.endDate).format('YYYY-MM-DD') : null } });
                        //}}
                        displayFormat="dd.MM.yyyy"
                        width="100%"
                        showClearButton={true}
                    />
                </div>
                <div className="filter-group">
                    <Label>Grupaj No</Label>
                    <TextBox
                        name="grupajNo"
                        value={filters.groupage_nos && filters.groupage_nos.length > 0 ? filters.groupage_nos[0] : ''}
                        onValueChanged={(e) => onFilterChange({ target: { name: 'groupage_nos', value: e.value ? [e.value] : [] } })}
                        placeholder="Grupaj no"
                        width="100%"
                    />
                </div>
            </div>
            <button
                className="search-btn mt-auto"
                onClick={onSearch}
                style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                }}
            >
                <i className="fa fa-list mr-2"></i> Listele
            </button>
        </div>
    </div>
);

/**
 * Summary Section Component
 * Displays summary statistics
 */
const SummarySection = ({ summary, onSummaryFilter, activeFilter }) => (
    <div className="summary-section h-100">
        <div className="summary-header">
            <i className="fa fa-info-circle"></i>
            <span>Özet Bilgiler</span>
            <small style={{ marginLeft: '2px', color: '#666', fontSize: '11px', fontWeight: 'normal' }}>
                <i> (Filtrelemek için tıklayın)</i>
            </small>
        </div>
        <div className="summary-body">
            <div className="summary-stats">
                {Object.entries(summary).map(([key, value]) => (
                    <div key={key} className={`stat-item stat-item-${key}`}
                        style={{
                            backgroundColor: activeFilter === key ? "rgb(239 245 255)" : "transparent",
                            cursor: key === "toplamYukSayisi" ? "default" : "pointer",
                            fontWeight: activeFilter === key ? "700" : "500",
                            color: activeFilter === key ? "rgb(0, 123, 255)" : "black"
                        }}
                        onClick={(e) => {
                            if (key === "toplamYukSayisi") {
                                return;
                            }
                            // Apply filter
                            onSummaryFilter(key);
                        }}
                    >
                        <div className="stat-row">
                            <div className="stat-label">{getSummaryLabel(key)}</div>
                            <div className="stat-value">{value}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

/**
 * Grid Actions Component
 * Renders action buttons for each row
 */
const GridActions = ({ data, onKaydet, onIndir, onIptal, colored, isDownloading, hasRole }) => (
    <div className="action-links">
        {
            hasRole("tedarikci_tir_write") &&
            <span
                className={`action-link ${data.isLocked ? 'disabled' : ''}`}
                onClick={() => !data.isLocked && onKaydet(data)}
                style={{
                    cursor: data.isLocked ? 'not-allowed' : 'pointer',
                    opacity: data.isLocked ? 0.5 : 1,
                    color: colored ? 'red' : data.isLocked ? '#999' : '#0056b3'
                }}
            >
                Kaydet
            </span>}

        <span
            className={`action-link`}
            onClick={() => !isDownloading && onIndir(data)}
            style={{
                cursor: isDownloading ? 'wait' : 'pointer',
                position: 'relative',
                width: '45px',
                display: 'inline-block'
            }}
        >
            <span style={{ color: isDownloading ? '#6c757d' : '#0056b3' }}>
                {isDownloading ? <i className="fa fa-spinner fa-spin" style={{ marginRight: '4px' }}></i> : 'İndir'}
            </span>
        </span>

        {hasRole("tedarikci_tir_write") && (
            <span
                className={`action-link ${!data.isLocked ? 'disabled' : ''}`}
                onClick={() => data.isLocked && onIptal(data)}
                style={{
                    cursor: !data.isLocked ? 'not-allowed' : 'pointer',
                    opacity: !data.isLocked ? 0.5 : 1
                }}
            >
                Tır Eşleme İptal
            </span>
        )}
    </div>
);

/**
 * Main TirEsleme Component
 */
const TirEsleme = () => {
    // State management

    const { hasRole } = useAuth(26)


    const [filters, setFilters] = useState({
        shipping_start: null,
        shipping_end: null,
        groupage_nos: []
    });
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState(INITIAL_SUMMARY_STATE);
    const [originalData, setOriginalData] = useState({});
    const [downloadsInProgress, setDownloadsInProgress] = useState(new Set());
    const dataGridRef = useRef(null);
    const [isExporting, setIsExporting] = useState(false);
    const [activeFilter, setActiveFilter] = useState(null);
    const [subcontractors, setSubcontractors] = useState([]);

    // Initialize date filters on mount
    // useEffect(() => {
    //     const startOfMonth = moment().startOf('month').format('YYYY-MM-DD');
    //     const endOfMonth = moment().endOf('month').format('YYYY-MM-DD');
    //     setFilters(prev => ({
    //         ...prev,
    //         shipping_start: startOfMonth,
    //         shipping_end: endOfMonth
    //     }));
    // }, []);

    // Fetch subcontractors on mount
    useEffect(() => {
        const fetchSubcontractors = async () => {
            try {
                const employeeId = getUserId();
                const response = await api.post('/employee/getEmployeeSubcontractors', {
                    employee_id: employeeId
                });
                const subcontractorsList = response?.data?.subcontractors || [];
                setSubcontractors(subcontractorsList);
            } catch (error) {
                console.error('Error fetching subcontractors:', error);
                toast.error('Taşeron firmalar yüklenirken hata oluştu');
            }
        };
        fetchSubcontractors();
    }, []);

    // Validation Functions
    const validateTruckAssignment = useCallback((data) => {
        if (!data.plaka) {
            toast.error(ERROR_MESSAGES.PLATE_EMPTY);
            return false;
        }

        if (!VALIDATION_RULES.PLATE.test(data.plaka.replaceAll(" ", "").toUpperCase())) {
            toast.error(ERROR_MESSAGES.PLATE_FORMAT);
            return false;
        }

        if (!data.sofor) {
            toast.error(ERROR_MESSAGES.DRIVER_EMPTY);
            return false;
        }

        return true;
    }, []);

    // Event Handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = async () => {
        try {
            setLoading(true);
            const response = await api.post('/getAllGroupagesForAgacli', {
                shipping_start: document.getElementById("shipping_date_planned").childNodes[0].childNodes[0].childNodes[0].value === "" ? null : document.getElementById("shipping_date_planned").childNodes[0].childNodes[0].childNodes[0].value,
                shipping_end: document.getElementById("shipping_date_planned").childNodes[2].childNodes[0].childNodes[0].value === "" ? null : document.getElementById("shipping_date_planned").childNodes[2].childNodes[0].childNodes[0].value,
                groupage_nos: filters.groupage_nos,
                employee_id: getUserId()
            });
            updateDataSourceAndSummary(response.data);
            // Store original data for revert functionality
            const originalDataMap = {};
            let res = response.data || []
            if (res.length === 0) {
                toast.warn("Grupaj bulunamadı...");
                return;
            }

            res.forEach(item => {
                originalDataMap[item.groupage_no] = {
                    plaka: item.plate_number || '',
                    sofor: item.driver_name || ''
                };
            });
            setOriginalData(originalDataMap);
        } catch (error) {
            let message = error.message.replace("failed to get groupages: failed to get groupages for Agacli: ", "Tır Eşleme: ") || 'Listeleme sırasında hata oluştu';
            console.error('Error searching:', error);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const handleKaydet = async (data) => {
        if (!validateTruckAssignment(data)) return;

        // subcontractor_id mantığı: seçilen subcontractor_id varsa onu kullan, yoksa truck_contractor, yoksa groupage_contractor, yoksa 9
        const subcontractor_id = data.subcontractor_id || data.truck_contractor || data.groupage_contractor || 9;

        try {
            const response = await api.post('/assignTruck', {
                groupage_no: data.grupajNo,
                plate_number: data.plaka.replaceAll(" ", "").toUpperCase(),
                subcontractor_id: subcontractor_id,
                driver_name: data.sofor,
                create_user_id: getUserId()
            });

            if (response.data.success) {
                updateDataSourceAfterAssignment(data);
                toast.success('Tır eşleme kaydedildi');
                refreshDataGrid();
            } else {
                toast.error(response.data.message || 'Bir hata oluştu');
            }
        } catch (error) {
            console.error('Error saving truck match:', error);
            toast.error('Tır eşleme kaydedilirken hata oluştu');
        }
    };

    const handleIndir = (data) => {
        // Add grupajNo to downloads in progress
        setDownloadsInProgress(prev => {
            const newSet = new Set(prev);
            newSet.add(data.grupajNo);
            return newSet;
        });

        // Call the pdf generator with row data including from_location and to_location
        generateAndDownloadGrupajPDF(data.grupajNo, {
            from_location: data.nereden,
            to_location: data.nereye
        })
            .finally(() => {
                setDownloadsInProgress(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(data.grupajNo);
                    return newSet;
                });
                // Refresh the DataGrid to update any UI changes
                refreshDataGrid();
            });
    };

    const handleIptal = async (data) => {
        try {
            const response = await api.post('/cancelTruckAssignment', {
                groupage_no: data.grupajNo
            });

            if (response.data.success) {
                updateDataSourceAfterCancellation(data);
                toast.success('Tır eşleme iptal edildi');
                refreshDataGrid();
            } else {
                toast.error(response.data.message || 'Bir hata oluştu');
            }
        } catch (error) {
            console.error('Error canceling truck match:', error);
            toast.error('Tır eşleme iptal edilirken hata oluştu');
        }
    };

    const handleRevert = () => {
        const updatedDataSource = dataSource.map(item => ({
            ...item,
            plaka: originalData[item.grupajNo]?.plaka || '',
            sofor: originalData[item.grupajNo]?.sofor || ''
        }));
        // Only keep records with aracSayisi > 0
        const filteredData = updatedDataSource.filter(item => item.aracSayisi > 0);
        setDataSource(filteredData);
        //  toast.info('Kaydedilmemiş değişiklikler geri alındı');
        refreshDataGrid();
    };

    const handleExportExcel = async () => {
        try {
            setIsExporting(true);

            if (dataGridRef.current.instance.getDataSource() === null) {
                toast.warning("Tabloda Veri Bulunamadı", {
                    position: "top-center",
                    autoClose: 3000
                })
                return
            }

            // Get filtered/visible data from DataGrid
            const visibleData = dataGridRef.current.instance.getVisibleRows().map(row => row.data);

            // Filter out records with aracSayisi = 0
            const filteredData = visibleData.filter(item => item.aracSayisi > 0);
            const allGroupageNos = filteredData.map(item => item.grupajNo);

            if (allGroupageNos.length === 0) {
                toast.warning("Aktarılacak veri bulunamadı", {
                    position: "top-center",
                    autoClose: 3000
                });
                return;
            }

            const response = await api.post("/getGroupageReport", {
                grupaj_nos: allGroupageNos
            });

            const data = response.data || [];
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Tır Eşleme Raporu');

            // Define columns with autoFilter
            worksheet.columns = [
                { header: 'GrupajNo', key: 'grupaj_no', width: 20 },
                { header: 'RotaAdi', key: 'rota_adi', width: 30 },
                { header: 'Sase', key: 'sase', width: 20 },
                { header: 'FromLokasyonAdi', key: 'from_lokasyon_adi', width: 25 },
                { header: 'ToLokasyonAdi', key: 'to_lokasyon_adi', width: 25 },
                { header: 'ModelAdi', key: 'model_adi', width: 20 },
                { header: 'RenkAdi', key: 'renk_adi', width: 20 },
                { header: 'PlanlamaTarih', key: 'planlama_tarih', width: 20 }
            ];

            // Style the header row
            const headerRow = worksheet.getRow(1);
            headerRow.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: '4472C4' }  // Blue background
                };
                cell.font = {
                    color: { argb: 'FFFFFF' },
                    bold: true
                };
                cell.alignment = {
                    horizontal: 'center',
                    vertical: 'middle'
                };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'D9D9D9' } },
                    left: { style: 'thin', color: { argb: 'D9D9D9' } },
                    bottom: { style: 'thin', color: { argb: 'D9D9D9' } },
                    right: { style: 'thin', color: { argb: 'D9D9D9' } }
                };
            });

            // Add data rows with alternating colors
            data.forEach((item, index) => {
                const row = worksheet.addRow(item);
                const isEvenRow = index % 2 === 0;

                worksheet.columns.forEach((col, colIndex) => {
                    const cell = row.getCell(colIndex + 1);
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: isEvenRow ? 'F2F2F2' : 'FFFFFF' }
                    };
                    cell.alignment = {
                        horizontal: 'center',
                        vertical: 'middle'
                    };
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'D9D9D9' } },
                        left: { style: 'thin', color: { argb: 'D9D9D9' } },
                        bottom: { style: 'thin', color: { argb: 'D9D9D9' } },
                        right: { style: 'thin', color: { argb: 'D9D9D9' } }
                    };
                });
            });

            // Enable AutoFilter
            worksheet.autoFilter = {
                from: { row: 1, column: 1 },
                to: { row: 1, column: worksheet.columns.length }
            };

            // Generate and download the file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Tir_Esleme_${moment().format('DD.MM.YYYY HH:mm:ss')}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Error exporting tir esleme report:", error);
            toast.error("Tır eşleme raporu oluşturulurken hata oluştu", {
                position: "top-center",
                autoClose: 3000
            });
        } finally {
            setIsExporting(false);
        }
    };

    // Helper Functions
    const updateDataSourceAndSummary = (responseData) => {
        const transformedData = responseData?.map(transformGroupageData);

        // Filter out records with aracSayisi of 0
        let filteredData = transformedData?.filter(item => item.aracSayisi > 0);

        // Eğer kullanıcının sadece 1 subcontractor'ı varsa, otomatik olarak seçili getir
        if (subcontractors.length === 1 && filteredData) {
            const singleSubcontractorId = subcontractors[0].id;
            filteredData = filteredData.map(item => ({
                ...item,
                // Sadece subcontractor_id null ise (truck_contractor ve groupage_contractor ikisi de null ise) otomatik set et
                subcontractor_id: item.subcontractor_id || singleSubcontractorId
            }));
        }

        setDataSource(filteredData);

        // Calculate filtered summary counts
        const nonZeroVehicles = responseData?.filter(item => item.vehicle_count > 0) || [];
        const readyNonZero = nonZeroVehicles.filter(item => item.status === "Sevke Hazır").length;
        const matchedNonZero = nonZeroVehicles.filter(item => item.plate_number && item.driver_name).length;
        const unmatchedNonZero = nonZeroVehicles.length - matchedNonZero;
        const readyAndMatchedNonZero = nonZeroVehicles.filter(item =>
            item.status === "Sevke Hazır" && item.plate_number && item.driver_name
        ).length;

        setSummary({
            toplamYukSayisi: numberWithCommas(nonZeroVehicles.length),
            sevkeHazirYukSayisi: numberWithCommas(readyNonZero),
            eslenenTirSayisi: numberWithCommas(matchedNonZero),
            eslenmemisTirSayisi: numberWithCommas(unmatchedNonZero),
            hazirYuklerdenEslenenSayisi: `${numberWithCommas(readyAndMatchedNonZero)}/${numberWithCommas(readyNonZero)}`
        });
    };

    const transformGroupageData = (item) => {
        // subcontractor_id belirleme mantığı:
        // truck_contractor varsa onu kullan, yoksa groupage_contractor, ikisi de null ise null
        let subcontractor_id = null;
        if (item.truck_contractor) {
            subcontractor_id = item.truck_contractor;
        } else if (item.groupage_contractor) {
            subcontractor_id = item.groupage_contractor;
        }

        return {
            sevkiyatTipi: item.shipping_type,
            grupajNo: item.groupage_no,
            planlananSevkTarihi: moment(item.shipping_date).format('DD.MM.YYYY'),
            aracSayisi: item.vehicle_count,
            nereden: item.from_location,
            nereye: item.to_location,
            durum: item.status,
            plaka: item.plate_number || '',
            sofor: item.driver_name || '',
            isLocked: Boolean(item.plate_number && item.driver_name),
            truck_contractor: item.truck_contractor,
            groupage_contractor: item.groupage_contractor,
            subcontractor_id: subcontractor_id
        };
    };

    const updateDataSourceAfterAssignment = (data) => {
        setDataSource(prev => prev.map(item =>
            item.grupajNo === data.grupajNo
                ? { ...item, isLocked: true }
                : item
        ));
    };

    const updateDataSourceAfterCancellation = (data) => {
        setDataSource(prev => prev.map(item =>
            item.grupajNo === data.grupajNo
                ? { ...item, isLocked: false, plaka: '', sofor: '' }
                : item
        ));
    };

    const getUserId = () => {
        return parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii'));
    };

    const refreshDataGrid = () => {
        if (dataGridRef.current) {
            // First refresh just the instance
            dataGridRef.current.instance.refresh();

            // Then force a repaint of the grid to ensure proper rendering
            dataGridRef.current.instance.repaint();

            // Update column widths to ensure proper layout
            dataGridRef.current.instance.updateDimensions();
        }
    };

    const handleSummaryFilter = (filterKey) => {
        // If clicking the same filter, clear it
        if (activeFilter === filterKey) {
            handleClearFilter();
            return;
        }

        setActiveFilter(filterKey);

        if (!dataGridRef.current) return;

        // Clear existing filters first
        dataGridRef.current.instance.clearFilter();

        // Apply filter based on the clicked summary item
        switch (filterKey) {
            case 'toplamYukSayisi':
                // Show all data - no filter needed
                break;
            case 'sevkeHazirYukSayisi':
                dataGridRef.current.instance.filter(['durum', '=', 'Sevke Hazır']);
                break;
            case 'eslenenTirSayisi':
                dataGridRef.current.instance.filter([
                    ['plaka', '<>', ''],
                    'and',
                    ['sofor', '<>', '']
                ]);
                break;
            case 'eslenmemisTirSayisi':
                dataGridRef.current.instance.filter([
                    ['plaka', '=', ''],
                    'or',
                    ['sofor', '=', '']
                ]);
                break;
            case 'hazirYuklerdenEslenenSayisi':
                dataGridRef.current.instance.filter([
                    ['durum', '=', 'Sevke Hazır'],
                    'and',
                    ['plaka', '<>', ''],
                    'and',
                    ['sofor', '<>', '']
                ]);
                break;
            default:
                break;
        }
    };

    const handleClearFilter = () => {
        setActiveFilter(null);
        if (dataGridRef.current) {
            dataGridRef.current.instance.clearFilter();
        }
        // Clear visual effects
        document.querySelectorAll(`.stat-item`).forEach(item => {
            item.style.backgroundColor = "transparent";
            item.style.fontWeight = "500";
        });
    };



    return (
        <>
            <Breadcrumb current="Tır Eşleme" />

            <Row className="mb-4">
                <Col md="6">
                    <FilterSection
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onSearch={handleSearch}
                    />
                </Col>
                <Col md="6">
                    <SummarySection
                        summary={summary}
                        onSummaryFilter={handleSummaryFilter}
                        activeFilter={activeFilter}
                    />
                </Col>
            </Row>

            <InfoSection
                handleRevert={handleRevert}
                handleExportExcel={handleExportExcel}
                isExporting={isExporting}
            />

            <DataGridSection
                dataSource={dataSource}
                onKaydet={handleKaydet}
                onIndir={handleIndir}
                onIptal={handleIptal}
                loading={loading}
                downloadsInProgress={downloadsInProgress}
                hasRole={hasRole}
                subcontractors={subcontractors}
                ref={dataGridRef}
            />

            <style jsx>{getStyles()}</style>
        </>
    );
};

/**
 * Info Section Component
 */
const InfoSection = ({ handleRevert, handleExportExcel, isExporting }) => (
    <Row className="mb-4 align-items-center">
        <Col md="8">
            <div className="info-text">
                <span className="info-label">Açıklama:</span> <b>SEVKE HAZIR</b> ifadesi grupajın bütün araçlarının peronda beklemesidir
            </div>
        </Col>
        <Col md="4" className="text-end d-flex justify-content-end gap-2">
            <button
                className="revert-btn"
                onClick={handleRevert}
                style={{
                    border: "1px solid #dee2e6",
                    background: "#ffffff",
                    color: "#6c757d",
                    padding: "8px",
                    minWidth: "40px",
                    cursor: "pointer",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "10px"
                }}
                title="Geri Al"
            >
                <i className="fa fa-undo"></i>
            </button>
            <button
                className="export-btn"
                onClick={handleExportExcel}
                disabled={isExporting}
                style={{
                    border: "1px solid #ddd",
                    background: "#47A046",
                    color: "white",
                    padding: "8px",
                    minWidth: "120px",
                    borderRadius: "4px",
                    cursor: isExporting ? "wait" : "pointer",
                    opacity: isExporting ? 0.7 : 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px"
                }}
            >
                {isExporting ? (
                    <>
                        <i className="fa fa-spinner fa-spin"></i>
                        Aktarılıyor...
                    </>
                ) : (
                    <>
                        <i className="fa fa-file-excel-o"></i>
                        Excel'e Aktar
                    </>
                )}
            </button>
        </Col>
    </Row>
);

/**
 * DataGrid Section Component
 */
const DataGridSection = React.forwardRef(({ dataSource, onKaydet, onIndir, onIptal, loading, downloadsInProgress, hasRole, subcontractors = [] }, ref) => (
    <div style={{ position: 'relative' }}>
        <LoadPanel
            // shadingColor="rgba(0,0,0,0.4)"
            position={{ of: '.custom-grid' }}
            visible={loading}
            showIndicator={true}
            shading={true}
            showPane={true}
        />
        <DataGrid
            dataSource={dataSource}
            showBorders={true}
            showColumnLines={true}
            showRowLines={true}
            columnAutoWidth={false}
            rowAlternationEnabled={true}
            keyExpr="grupajNo"
            className="custom-grid"
            height={600}
            editing={{
                mode: 'cell',
                allowUpdating: true
            }}
            noDataText="Veri yok"
            filterRow={{ visible: true }}
            //  filterPanel={{ visible: true }}

            allowColumnResizing={true}
            columnResizingMode="widget"
            ref={ref}
        >
            <Column dataField="sevkiyatTipi" caption="Sevkiyat Tipi" width={110} allowEditing={false} />
            <Column dataField="grupajNo" caption="Grupaj No" width={125} allowEditing={false}
                cellRender={(e) => {
                    return (
                        <div>
                            <span>{e.data.grupajNo}</span>
                        </div>
                    )
                }}
            />
            <Column
                dataField="planlananSevkTarihi"
                caption="Planlanan Sevk Tarihi"
                width={100}
                allowEditing={false}
                headerCellRender={() => (
                    <div><span>Planlanan <br /> Sevk Tarihi</span></div>
                )}
            />
            <Column
                dataField="aracSayisi"
                caption="Araç Sayısı"
                width={65}
                alignment="center"
                allowEditing={false}
                headerCellRender={() => (
                    <div style={{ textAlign: 'center' }}><span>Araç<br />Sayısı</span></div>
                )}
            />
            <Column dataField="nereden" caption="Nereden" width={180} allowEditing={false} />
            <Column dataField="nereye" caption="Nereye" width={150} allowEditing={false} />
            <Column
                dataField="durum"
                caption="Durum"
                width={100}
                allowEditing={false}
                lookup={{
                    dataSource: [
                        { id: "Sevke Hazır", text: "Sevke Hazır" },
                        { id: "", text: "" }
                    ],
                    displayExpr: "text",
                    valueExpr: "id"
                }}
                cellRender={(e) => (
                    <div style={getDurumStyle(e.data.durum)}>
                        <span>{e.data.durum}</span>
                    </div>
                )}
            />
            <Column
                dataField="subcontractor_id"
                caption="Taşeron Firma"
                width={140}
                allowEditing={(rowData) => !rowData.isLocked}
                lookup={{
                    dataSource: subcontractors,
                    displayExpr: "name",
                    valueExpr: "id"
                }}
            />
            <Column
                dataField="plaka"
                caption="Plaka"
                width={130}
                allowEditing={(rowData) => !rowData.isLocked}
            />
            <Column
                dataField="sofor"
                caption="Şoför"
                width={180}
                allowEditing={(rowData) => !rowData.isLocked}
            />

            <Column
                caption="İşlemler"
                cellRender={(data) => {
                    let colored = !data.data.isLocked && (data.data.plaka !== "" || data.data.sofor !== "");
                    const isDownloading = downloadsInProgress.has(data.data.grupajNo);
                    return (
                        <GridActions
                            data={data.data}
                            onKaydet={onKaydet}
                            onIndir={onIndir}
                            onIptal={onIptal}
                            colored={colored}
                            isDownloading={isDownloading}
                            hasRole={hasRole}
                        />
                    )
                }}
                minWidth={250}
                alignment="center"
                allowEditing={false}

            />
        </DataGrid>
    </div>
));

// Helper Functions
const getSummaryLabel = (key) => {
    const labels = {
        toplamYukSayisi: 'Toplam Yük Sayısı',
        sevkeHazirYukSayisi: 'Sevke Hazır Yük Sayısı',
        eslenenTirSayisi: 'Eşlenen Yük Sayısı',
        eslenmemisTirSayisi: 'Eşlenmemiş Yük Sayısı',
        hazirYuklerdenEslenenSayisi: 'Hazır Yüklerden Eşlenen Sayısı'
    };
    return labels[key] || key;
};

const getDurumStyle = (durum) => ({
    backgroundColor: durum === 'Sevke Hazır' ? '#e8f5e9' : 'transparent',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    color: durum === 'Sevke Hazır' ? '#2e7d32' : '#6c757d'
});

// Styles
const getStyles = () => `
                .filter-section, .summary-section {
                    background: white;
                    border: 1px solid #e0e0e0;
                    border-radius: 6px;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                }
                .filter-header, .summary-header {
                    padding: 12px 15px;
                    border-bottom: 1px solid #e0e0e0;
                    font-weight: 500;
                    color: #444;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #f8f9fa;
                    border-radius: 6px 6px 0 0;
                }
                .filter-body {
                    padding: 15px;
                    flex-grow: 1;
                }
                .filter-group {
                    margin-bottom: 15px;
                }
                .filter-group label {
                    font-size: 13px;
                    color: #666;
                    margin-bottom: 4px;
                }
                .summary-body {
                    padding: 12px;
                    flex-grow: 1;
                }
                .summary-stats {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .stat-item {
                    padding: 4px 10px;
                    border-bottom: 1px solid #f0f0f0;
                }
                .stat-item:hover:not(.stat-item-toplamYukSayisi) {
                    background-color:rgb(255, 244, 212) !important;
                }
                .stat-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .stat-label {
                    font-size: 13px;
                }
                .stat-value {
                    font-size: 14px;
                    font-weight: 600;
                    color: #2c3e50;
                    min-width: 60px;
                    text-align: right;
                    margin-left: 15px;
                }
                .info-text {
                    font-size: 13px;
                    color: #666;
                    padding: 8px 12px;
                    background: #fff3cd;
                    border: 1px solid #ffeeba;
                    border-radius: 4px;
                    margin: 0;
                }
                .info-label {
                    font-weight: 500;
                    margin-right: 5px;
                }
                .custom-grid {
                    background: white;
                    border: 1px solid #e0e0e0;
                    border-radius: 4px;
                }
                .action-links {
                    display: flex;
                    gap: 0;
                    justify-content: center;
                    width: 100%;
                }
                .action-link {
                    color: #0056b3;
                    cursor: pointer;
                    font-size: 13px;
                    font-weight: 600;
                    text-decoration: none;
                    transition: color 0.2s;
                    padding: 0 8px;
                    position: relative;
                    min-width: 60px;
                    text-align: center;
                }
                .action-link:not(:last-child)::after {
                    content: '|';
                    position: absolute;
                    right: -2px;
                    color: #dee2e6;
                    font-size: 12px;
                    font-weight: 900;
                }
                .action-link:hover {
                    color: #003d82;
                    text-decoration: underline;
                }
                .action-link.disabled {
                    color: #999;
                    pointer-events: none;
                }
                .summary-table {
                    table-layout: fixed !important;
                    border-collapse: collapse;
                }
                
                .summary-table th:nth-child(2),
                .summary-table th:nth-child(4) {
                    width: 40px !important;
                    max-width: 40px !important;
                    min-width: 40px !important;
                    box-sizing: border-box !important;
                }
                
                .summary-table td:nth-child(2),
                .summary-table td:nth-child(4) {
                    width: 40px !important;
                    max-width: 40px !important;
                    min-width: 40px !important;
                    box-sizing: border-box !important;
                    white-space: nowrap !important;
                    overflow: hidden !important;
                    text-align: center !important;
                }
                
                .summary-table th:nth-child(1),
                .summary-table th:nth-child(3) {
                    width: calc(50% - 20px) !important;
                }
                
                .summary-table td:nth-child(1),
                .summary-table td:nth-child(3) {
                    width: calc(50% - 20px) !important;
                }

                .adet-column {
                    width: 40px !important;
                    min-width: 40px !important;
                    max-width: 40px !important;
                    text-align: center !important;
                }
                .address-column {
                    width: auto !important;
                }
                .action-link.downloading {
                    color: #6c757d;
                }
`;

export default TirEsleme;